/**
 * Stage 31 · Continuous Compliance & Evidence — Trust Centre.
 * Never displays "compliant" just because data is missing.
 */
import { orgLabel } from "@/lib/orgLabel";
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, PlayCircle, Calendar, AlertOctagon, FileSpreadsheet, Building2 } from "lucide-react";

const RESULT_STYLES = {
  pass: "bg-emerald-100 text-emerald-800 border-emerald-200",
  fail: "bg-rose-100 text-rose-800 border-rose-200",
  exception: "bg-amber-100 text-amber-800 border-amber-200",
  insufficient_evidence: "bg-slate-100 text-slate-600 border-slate-200",
  not_applicable: "bg-slate-100 text-slate-500 border-slate-200",
  review_required: "bg-sky-100 text-sky-800 border-sky-200",
};

export default function Trust() {
  const [tab, setTab] = useState("controls");
  const [mc, setMc] = useState(""); const [masters, setMasters] = useState([]); const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setMe(data); if (data?.master_company_id) setMc(data.master_company_id); });
    api.get("/master-companies").then(({ data }) => setMasters(data.items || []));
  }, []);
  const isSuntronix = me && !me.master_company_id;
  return (
    <div className="max-w-6xl space-y-5" data-testid="trust-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Trust · Controls & Evidence</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Continuous Compliance</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Every conclusion links to an actual transaction. Never displays COMPLIANT just because data is missing — pass, fail, exception, insufficient evidence, or review required.
        </p>
      </div>
      {isSuntronix && (
        <select className="input min-w-[240px]" value={mc} onChange={(e) => setMc(e.target.value)} data-testid="trust-tenant">
          <option value="">All tenants</option>
          {masters.map((m) => <option key={m.id} value={m.id}>{orgLabel(m, masters)}</option>)}
        </select>
      )}
      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {[
          { key: "controls",   label: "Controls",       icon: ShieldCheck },
          { key: "tests",      label: "Test results",   icon: PlayCircle },
          { key: "exceptions", label: "Exceptions",     icon: AlertOctagon },
          { key: "calendar",   label: "Calendar",       icon: Calendar },
          { key: "audit",      label: "Audit pack",     icon: FileSpreadsheet },
          { key: "customer",   label: "Customer view",  icon: Building2 },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`trust-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {tab === "controls"   && <ControlsTab mc={mc} isSuntronix={isSuntronix} />}
      {tab === "tests"      && <TestsTab mc={mc} />}
      {tab === "exceptions" && <ExceptionsTab mc={mc} />}
      {tab === "calendar"   && <CalendarTab mc={mc} />}
      {tab === "audit"      && <AuditPackTab mc={mc} />}
      {tab === "customer"   && <CustomerViewTab mc={mc} />}
    </div>
  );
}

function ResultChip({ v }) {
  return <span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (RESULT_STYLES[v] || RESULT_STYLES.review_required)}>{(v || "").replace(/_/g, " ")}</span>;
}

/* --------- Controls --------- */
function ControlsTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [enums, setEnums] = useState({ evidence_types: [], framework_kinds: [], result_values: [] });
  const load = useCallback(() => api.get("/trust/controls", { params: mc ? { master_company_id: mc } : {} })
    .then(({ data }) => { setItems(data.items || []); setEnums({ evidence_types: data.evidence_types || [], framework_kinds: data.framework_kinds || [], result_values: data.result_values || [] }); })
    .catch(() => {}), [mc]);
  useEffect(() => { load(); }, [load]);
  const runTest = async (cid) => {
    try { const { data } = await api.post(`/trust/controls/${cid}/test`, null, { params: mc ? { master_company_id: mc } : {} });
          toast.success(`Test ${data.result} · ${data.passing}/${data.population}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail || "Test failed")); }
  };
  return (
    <div className="space-y-3" data-testid="trust-controls">
      {isSuntronix && (
        <div className="flex justify-end">
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="trust-control-new">New control</button>
        </div>
      )}
      {showForm && <ControlForm enums={enums} onCancel={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">ID</th><th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">Category</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">v</th><th className="text-right py-2 px-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500">No controls yet.</td></tr>
            ) : items.map((c) => (
              <tr key={c.control_id} className="border-t border-slate-100" data-testid={`trust-ctrl-${c.control_id}`}>
                <td className="py-2 px-3 font-mono text-slate-900">{c.control_id}</td>
                <td className="py-2 px-3 text-slate-800">{c.name}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">{c.category || "—"}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">{c.status}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">v{c.version || 1}</td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => runTest(c.control_id)} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`trust-ctrl-test-${c.control_id}`}>Run test</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function ControlForm({ enums, onCancel, onSaved }) {
  const [f, setF] = useState({
    control_id: "", name: "", control_objective: "", category: "itad",
    evidence_requirement: [], frameworks: [],
    frequency: "monthly", threshold_pass_pct: 100, status: "active",
    population_collection: "assets", population_filter: "{}", passing_filter: "{}",
  });
  const save = async () => {
    try {
      let popF = {}; let passF = {};
      try { popF = JSON.parse(f.population_filter || "{}"); passF = JSON.parse(f.passing_filter || "{}"); }
      catch { toast.error("Filters must be valid JSON"); return; }
      await api.post("/trust/controls", { ...f, population_filter: popF, passing_filter: passF });
      toast.success("Control saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="trust-control-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Control ID</div><input className="input" value={f.control_id} onChange={(e) => setF({ ...f, control_id: e.target.value })} placeholder="ITAD-001" data-testid="trust-cf-id" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="trust-cf-name" /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Objective</div><textarea className="input min-h-[50px]" value={f.control_objective} onChange={(e) => setF({ ...f, control_objective: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Category</div><input className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Threshold pass %</div><input type="number" className="input" value={f.threshold_pass_pct} onChange={(e) => setF({ ...f, threshold_pass_pct: Number(e.target.value) })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Population collection</div><input className="input" value={f.population_collection} onChange={(e) => setF({ ...f, population_collection: e.target.value })} placeholder="assets" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Frequency</div><select className="input" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}>{["continuous","daily","weekly","monthly","quarterly","annual"].map((k) => <option key={k}>{k}</option>)}</select></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Population filter (JSON)</div><textarea className="input min-h-[50px] font-mono text-xs" value={f.population_filter} onChange={(e) => setF({ ...f, population_filter: e.target.value })} placeholder='{"lifecycle_state":"awaiting_erasure"}' /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Passing filter (JSON)</div><textarea className="input min-h-[50px] font-mono text-xs" value={f.passing_filter} onChange={(e) => setF({ ...f, passing_filter: e.target.value })} placeholder='{"has_wipe_certificate":true}' /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
        <button onClick={save} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="trust-cf-save">Save control</button>
      </div>
    </div>
  );
}

/* --------- Tests --------- */
function TestsTab({ mc }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/trust/tests", { params: mc ? { master_company_id: mc } : {} }).then(({ data }) => setItems(data.items || [])); }, [mc]);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="trust-tests">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Control</th><th className="text-right py-2 px-3">Population</th><th className="text-right py-2 px-3">Pass</th><th className="text-right py-2 px-3">Fail</th><th className="text-left py-2 px-3">Result</th><th className="text-left py-2 px-3">Executed</th></tr></thead>
        <tbody>
          {items.length === 0 ? <tr><td colSpan={6} className="py-6 text-center text-slate-500">No tests executed yet.</td></tr>
            : items.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900">{t.control_id}</td>
                <td className="py-2 px-3 text-right tabular-nums">{t.population}</td>
                <td className="py-2 px-3 text-right tabular-nums text-emerald-700">{t.passing}</td>
                <td className="py-2 px-3 text-right tabular-nums text-rose-700">{t.failing}</td>
                <td className="py-2 px-3"><ResultChip v={t.result} /></td>
                <td className="py-2 px-3 text-slate-600 text-xs">{t.executed_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------- Exceptions --------- */
function ExceptionsTab({ mc }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/trust/exceptions", { params: mc ? { master_company_id: mc } : {} }).then(({ data }) => setItems(data.items || [])), [mc]);
  useEffect(() => { load(); }, [load]);
  const accept = async (id) => {
    const reason = window.prompt("Reason for risk acceptance?"); if (!reason) return;
    const approver = window.prompt("Approver?"); if (!approver) return;
    try { await api.post(`/trust/exceptions/${id}/update`, { status: "accepted", accepted_reason: reason, approver }); toast.success("Risk accepted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="trust-exceptions">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Control</th><th className="text-right py-2 px-3">Failing</th><th className="text-left py-2 px-3">Severity</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">Reason</th><th className="text-right py-2 px-3"></th></tr></thead>
        <tbody>
          {items.length === 0 ? <tr><td colSpan={6} className="py-6 text-center text-slate-500">No open exceptions.</td></tr>
            : items.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900">{e.control_id}</td>
                <td className="py-2 px-3 text-right tabular-nums text-rose-700">{e.failing_count}</td>
                <td className="py-2 px-3 text-slate-700 text-xs">{e.severity}</td>
                <td className="py-2 px-3"><ResultChip v={e.status === "open" ? "fail" : e.status === "accepted" ? "exception" : "pass"} /></td>
                <td className="py-2 px-3 text-slate-800">{e.reason}</td>
                <td className="py-2 px-3 text-right">
                  {e.status === "open" && <button onClick={() => accept(e.id)} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`trust-exc-accept-${e.id}`}>Accept risk</button>}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------- Calendar --------- */
function CalendarTab({ mc }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/trust/calendar", { params: { days_forward: 90, master_company_id: mc || undefined } }).then(({ data }) => setItems(data.items || [])); }, [mc]);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="trust-calendar">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">When</th><th className="text-left py-2 px-3">Kind</th><th className="text-left py-2 px-3">Detail</th></tr></thead>
        <tbody>
          {items.length === 0 ? <tr><td colSpan={3} className="py-6 text-center text-slate-500">No calendar entries in the next 90 days.</td></tr>
            : items.map((c, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-2 px-3 text-slate-700 text-xs">{(c.at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="py-2 px-3 font-mono text-slate-700">{c.kind}</td>
                <td className="py-2 px-3 text-slate-800">{c.control_id || c.title || c.review_id || ""}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------- Audit pack --------- */
function AuditPackTab({ mc }) {
  const [cid, setCid] = useState("");
  const [pack, setPack] = useState(null);
  const generate = async () => {
    if (!cid.trim()) { toast.error("Enter control id"); return; }
    try { const { data } = await api.get(`/trust/audit-pack/${cid.trim()}`, { params: mc ? { master_company_id: mc } : {} });
          setPack(data); toast.success("Audit pack generated (snapshot stored)"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="trust-audit">
      <div className="flex items-end gap-2">
        <label className="flex-1"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Control ID</div><input className="input w-full" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="trust-audit-cid" /></label>
        <button onClick={generate} className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm" data-testid="trust-audit-generate">Generate</button>
      </div>
      {pack && (
        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
          <div className="text-sm font-medium">{pack.control?.name} · v{pack.control?.version}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Tests" value={pack.tests?.length} />
            <Tile label="Exceptions" value={pack.exceptions?.length} />
            <Tile label="Risk acceptances" value={pack.risk_acceptances?.length} />
            <Tile label="Generated" value={pack.generated_at?.slice(0, 16).replace("T", " ")} />
          </div>
          <details className="text-xs"><summary className="cursor-pointer text-slate-500 uppercase tracking-widest">Raw snapshot</summary><pre className="bg-slate-50 border border-slate-100 rounded p-2 mt-2 overflow-auto max-h-64">{JSON.stringify(pack, null, 2)}</pre></details>
        </div>
      )}
    </div>
  );
}

/* --------- Customer view --------- */
function CustomerViewTab({ mc }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (!mc) return; api.get("/trust/customer-view", { params: { master_company_id: mc } }).then(({ data }) => setD(data)).catch(() => setD(null)); }, [mc]);
  if (!mc) return <div className="text-sm text-slate-500">Select a tenant to see the customer trust view.</div>;
  if (!d)  return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="trust-customer">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Controls monitored" value={d.tiles?.controls_monitored} />
        <Tile label="Recent tests"       value={d.tiles?.recent_tests} />
        <Tile label="Open exceptions"    value={d.tiles?.open_exceptions} tone={d.tiles?.open_exceptions ? "warn" : "ok"} />
        <Tile label="Accepted risk"      value={d.tiles?.accepted_risk} />
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-200 text-sm font-medium">Recent results</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Control</th><th className="text-right py-2 px-3">Pass %</th><th className="text-left py-2 px-3">Result</th><th className="text-left py-2 px-3">Executed</th></tr></thead>
          <tbody>
            {(d.recent_results || []).length === 0 ? <tr><td colSpan={4} className="py-6 text-center text-slate-500">No results yet.</td></tr>
              : d.recent_results.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-2 px-3 font-mono">{r.control_id}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.pass_pct ?? "—"}</td>
                  <td className="py-2 px-3"><ResultChip v={r.result} /></td>
                  <td className="py-2 px-3 text-slate-600 text-xs">{r.executed_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const cls = tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-2xl font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
    </div>
  );
}
